import { useState, useEffect, useMemo } from 'react';
import { format, addMonths, addYears, addDays, isValid } from 'date-fns';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { ContractType, ContactPerson, Category, Currency } from '@/lib/types';
import { CompanyDetails } from '@/lib/api/companyRegister';

export interface Attachment {
  id?: string;
  name: string;
  url: string;
  type: string;
  size: number;
}

export const useContractFormState = (contract?: any) => {
  // Extract contact IDs from contract.contactPersons if available
  const initialContactIds = useMemo(() => {
    if (contract?.contactPersons?.length > 0) {
      return contract.contactPersons.map((contact: ContactPerson) => contact.id);
    }
    return [];
  }, [contract]);

  const [selectedContacts, setSelectedContacts] = useState<string[]>(initialContactIds);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    contract?.attachments || []
  );
  const [contractTypeModalOpen, setContractTypeModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [currencyModalOpen, setCurrencyModalOpen] = useState(false);
  const [selectedContractType, setSelectedContractType] = useState<ContractType | null>(
    contract?.type || null
  );

  // Fetch user preferences for default currency
  const { useUserPreferencesQuery } = useUserPreferences();
  const { data: userPreferences } = useUserPreferencesQuery();

  // Calculate renewal date if contract type has auto-renewal
  const renewalDate = useMemo(() => {
    if (contract && selectedContractType && selectedContractType.auto_renews) {
      // Make sure we have a valid signed date to work with
      let signedDate: Date;
      
      if (contract.signedDate) {
        // Convert string date to Date object if needed
        signedDate = contract.signedDate instanceof Date 
          ? contract.signedDate 
          : new Date(contract.signedDate);
      } else {
        signedDate = new Date(); // Use current date as fallback
      }
      
      // Verify we have a valid date before proceeding
      if (!isValid(signedDate)) {
        return null;
      }
      
      const interval = selectedContractType.renewal_interval || 1;
      const unit = selectedContractType.renewal_unit || 'Year';
      
      try {
        if (unit === 'Day') {
          return addDays(signedDate, interval);
        } else if (unit === 'Month') {
          return addMonths(signedDate, interval);
        } else if (unit === 'Year') {
          return addYears(signedDate, interval);
        }
      } catch (error) {
        console.error("Error calculating renewal date:", error);
        return null;
      }
    }
    return null;
  }, [contract, selectedContractType]);

  // Set up default form values
  const [formDefaultValues, setFormDefaultValues] = useState(() => {
    if (contract) {
      return {
        supplierName: contract.supplierName || '',
        organisationnumber: contract.organisationnumber || '',
        contractTypeId: contract.type?.id || '',
        categoryId: contract.categoryId || '',
        expectedCost: contract.expectedCost || 0,
        currencyId: contract.currencyId || userPreferences?.defaultCurrencyId || '',
        signedDate: contract.signedDate ? new Date(contract.signedDate) : new Date(),
        expiryDate: contract.expiryDate ? new Date(contract.expiryDate) : addYears(new Date(), 1),
        paymentFrequencyInterval: contract.paymentFrequency?.interval || 1,
        paymentFrequencyUnit: contract.paymentFrequency?.unit || 'Month',
        importance: contract.importance || 3,
        reminderPeriod: contract.reminderPeriod || '30days',
        reminderFrequency: contract.reminderFrequency || 'Once',
        use_services_cost: contract.use_services_cost || false,
        paymentType: contract.paymentType || 'BankTransfer',
      };
    }
    
    return {
      supplierName: '',
      organisationnumber: '',
      contractTypeId: '',
      categoryId: '',
      expectedCost: 0,
      currencyId: userPreferences?.defaultCurrencyId || '',
      signedDate: new Date(),
      expiryDate: addYears(new Date(), 1),
      paymentFrequencyInterval: 1,
      paymentFrequencyUnit: 'Month' as const,
      importance: 3,
      reminderPeriod: '30days',
      reminderFrequency: 'Once',
      use_services_cost: false,
      paymentType: 'BankTransfer' as const,
    };
  });

  // Handle the creation and selection of various entities
  const handleContractTypeCreated = (contractType: ContractType) => {
    setSelectedContractType(contractType);
    return contractType;
  };

  const handleCategoryCreated = (category: Category) => {
    return category;
  };

  const handleContactCreated = (contact: ContactPerson) => {
    setSelectedContacts(prev => [...prev, contact.id]);
    return contact;
  };

  const handleCurrencyCreated = (currency: Currency) => {
    return currency;
  };

  const handleContactSelection = (contactIds: string[]) => {
    setSelectedContacts(contactIds);
  };

  const handleCompanySelect = (company: CompanyDetails) => {
    return company;
  };

  // Update default currency when user preferences change
  useEffect(() => {
    if (!contract && userPreferences?.defaultCurrencyId) {
      // Only update if we're in create mode and user preferences are loaded
      setFormDefaultValues(prev => ({
        ...prev,
        currencyId: userPreferences.defaultCurrencyId
      }));
    }
  }, [userPreferences, contract]);

  // Update selected contacts when contract changes
  useEffect(() => {
    if (contract?.contactPersons?.length > 0) {
      setSelectedContacts(contract.contactPersons.map((contact: ContactPerson) => contact.id));
    }
  }, [contract]);

  return {
    selectedContacts,
    setSelectedContacts,
    attachments,
    setAttachments,
    existingAttachments,
    setExistingAttachments,
    contractTypeModalOpen,
    setContractTypeModalOpen,
    categoryModalOpen,
    setCategoryModalOpen,
    contactModalOpen,
    setContactModalOpen,
    currencyModalOpen,
    setCurrencyModalOpen,
    selectedContractType,
    setSelectedContractType,
    renewalDate,
    formDefaultValues,
    handleContractTypeCreated,
    handleCategoryCreated,
    handleContactCreated,
    handleCurrencyCreated,
    handleContactSelection,
    handleCompanySelect
  };
};
